/**
 * The account-filter lookups. These are the UI-state terms that `domain/`
 * deliberately refuses to read for itself (D25).
 *
 * Everything here takes state as an argument. No store import, no module-level
 * mutable state, no memo — see `README.md` for why the old client's memo has no
 * counterpart here.
 */
import { countsForMetrics, type MetricsContext } from '@domain/trade';

/**
 * Sentinel selection id for trades belonging to NO account — `accountId` is null,
 * or points at an account that no longer exists.
 *
 * Without it these trades are invisible under every per-account filter and only
 * surface under "All accounts", mixed in with everything else. Selecting the
 * sentinel isolates them so they can be reassigned in bulk.
 */
export const UNASSIGNED_FILTER_ID = '__unassigned__';

export interface AccountFilterAccount {
  id: string;
  excludeFromMetrics?: boolean;
}

export interface AccountFilterTrade {
  accountId: string | null;
}

/**
 * The user's account selection. **An empty array means "all accounts"** — it is
 * not an empty result. Everything in this module reads it that way.
 */
export type AccountSelection = readonly string[];

export function isAccountExcluded(
  accountId: string | null | undefined,
  accounts: readonly AccountFilterAccount[],
): boolean {
  if (!accountId) return false;
  return !!accounts.find((a) => a.id === accountId)?.excludeFromMetrics;
}

/** A trade whose account is null or no longer exists. */
export function isUnassignedTrade(
  t: AccountFilterTrade,
  knownAccountIds: ReadonlySet<string>,
): boolean {
  return !t.accountId || !knownAccountIds.has(t.accountId);
}

/**
 * Is the CURRENT selection nothing but excluded accounts?
 *
 * When it is, the metrics exclusion lifts for that view: the user has deliberately
 * drilled into SIM / Replay and wants to see how it went, and showing every number
 * blank on an account whose trades are listed right there reads as broken.
 *
 * ⚠ Deliberately NOT "is an excluded account selected". Mixing a SIM account into a
 * multi-select alongside real ones would then silently move real P&L — precisely
 * what the exclude flag exists to prevent. Excluded-ONLY keeps the drill-in useful
 * and makes contamination impossible.
 *
 * Two selections answer `false` before any account is inspected:
 *   - empty ("All accounts") — the exclusion must hold across a mixed book
 *   - one containing the unassigned sentinel — unassigned is not an excluded account
 */
export function selectionIsExcludedOnly(
  selection: AccountSelection,
  accounts: readonly AccountFilterAccount[],
): boolean {
  if (!selection.length) return false;
  if (selection.includes(UNASSIGNED_FILTER_ID)) return false;
  return selection.every((id) => isAccountExcluded(id, accounts));
}

/**
 * Trades belonging to the selected accounts. Ignores date and facet filters —
 * those are separate lenses and several views deliberately bypass them.
 */
export function tradesForSelection<T extends AccountFilterTrade>(
  trades: readonly T[],
  selection: AccountSelection,
  accounts: readonly AccountFilterAccount[],
): readonly T[] {
  if (!selection.length) return trades;
  const set = new Set(selection);
  const wantUnassigned = set.has(UNASSIGNED_FILTER_ID);
  const known = wantUnassigned ? new Set(accounts.map((a) => a.id)) : new Set<string>();
  return trades.filter(
    (t) =>
      (t.accountId != null && set.has(t.accountId)) ||
      (wantUnassigned && isUnassignedTrade(t, known)),
  );
}

/**
 * Build the `MetricsContext` a trade needs, having resolved the selection term
 * ONCE for the whole population.
 *
 * The returned function is the boundary this layer exists to draw: everything
 * UI-dependent has been evaluated by the time `domain/countsForMetrics` is
 * reached, so the rule itself stays callable from a server with no browser.
 */
export function metricsContextFactory(
  accounts: readonly AccountFilterAccount[],
  selection: AccountSelection,
): (t: AccountFilterTrade) => MetricsContext {
  const excludedOnly = selectionIsExcludedOnly(selection, accounts);
  const excludedIds = new Set(
    accounts.filter((a) => a.excludeFromMetrics).map((a) => a.id),
  );
  return (t) => ({
    accountExcluded: t.accountId != null && excludedIds.has(t.accountId),
    selectionIsExcludedOnly: excludedOnly,
  });
}

/**
 * The old client's `tradesForActiveAccounts().filter(countsForMetrics)` — one
 * named seam, because it is the population EVERY analytics surface starts from.
 *
 * ⚠ THE TWO STEPS ARE NOT REDUNDANT, and each has been left out once:
 *
 *   1. `tradesForSelection` — what the user is looking at.
 *   2. `countsForMetrics`   — what is allowed to move a number. Without it a SIM
 *      or replay account's trades land in every aggregate, which is the entire
 *      thing the exclude-from-metrics flag exists to prevent. Setup Quality
 *      shipped without it and its leaderboard counted practice trades as edge.
 *
 * ⚠ NO FACET FILTER. The old client applies facets only through
 * `tradesForActive()`; analytics reaches its population through
 * `tradesForActiveAccounts()`, which bypasses the facet lens AND the date filter
 * on purpose. A pattern leaderboard filtered to one pattern is not a leaderboard.
 */
export function tradesCountingForMetrics<T extends AccountFilterTrade>(
  trades: readonly T[],
  selection: AccountSelection,
  accounts: readonly AccountFilterAccount[],
): readonly T[] {
  const inView = tradesForSelection(trades, selection, accounts);
  const ctxFor = metricsContextFactory(accounts, selection);
  return inView.filter((t) => countsForMetrics(t, ctxFor(t)));
}

// ── Account types, re-exported for ui/ ───────────────────────────────────────
//
// ⚠ Same rule as MetricsRow in population.ts: `ui/` may not import `domain/` at
// all, and naming a type still counts as an import. The ban stays mechanical
// because "ui/ imports nothing from domain/" is checkable while "only types" is
// an argument at every review.
//
// This is also the honest dependency. What a component needs is "the account
// shape selectors hands me", not the domain's internal model — and routing it
// through here means the day those diverge, the UI follows the selector.
export type { Account, AccountAdjustment, AccountRules } from '../domain/types';

// ── The facet lens, re-exported for ui/ ──────────────────────────────────────
//
// ⚠ Same mechanical rule as everything else here: `ui/` may not import
// `domain/`, so the filter drawer reaches its own predicate through this layer
// or not at all. Without the re-export the path of least resistance for a
// component is to reimplement the check inline — and the inline version is the
// one that reads `t.date` instead of the effective date.
export {
  ANNOTATION_OPTS,
  annotationOptions,
  applyFacets,
  dayOfWeekOptions,
  instrumentOptions,
  instrumentRoot,
  isAnnotationValue,
  parseTagRegistry,
  patternOptions,
  pruneUnknownPatterns,
  pruneUnknownTags,
  tagOptions,
  tradeDayOfWeek,
  tradePassesFacets,
  tradeTagIds,
} from '../domain/facets';
export type { AnnotationOption, FacetSelection, FacetTrade, UserTag } from '../domain/facets';
